import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { reviewsService } from "api/services/reviews.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Table, { Column } from "components/UI/Table";
import Toggle from "components/UI/Toggle";
import { extractList, getApiErrorMessage } from "utils/api-error";

import type { Review } from "api/types/reviews.types";

import "pages/AdminPage.scss";
import "./ReviewManagement.scss";

const ReviewManagement: React.FC = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [ratingFilter, setRatingFilter] = useState("");
  const [blindFilter, setBlindFilter] = useState("");
  const [error, setError] = useState("");

  // 삭제 모달
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    review: Review | null;
  }>({ open: false, review: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchReviews = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await reviewsService.adminList();
      setReviews(extractList<Review>(res.data));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const filteredReviews = reviews.filter((r) => {
    if (ratingFilter && String(r.rating) !== ratingFilter) return false;
    if (blindFilter === "blinded" && !r.is_blind) return false;
    if (blindFilter === "visible" && r.is_blind) return false;
    if (search) {
      const q = search.toLowerCase();
      return r.user_name.toLowerCase().includes(q) || r.product_name.toLowerCase().includes(q) || r.content.toLowerCase().includes(q);
    }
    return true;
  });

  const handleToggleBlind = async (reviewId: number, isBlind: boolean) => {
    try {
      await reviewsService.toggleBlind(reviewId, isBlind);
      fetchReviews();
    } catch (err) {
      alert(getApiErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.review) return;
    setDeleteLoading(true);
    try {
      await reviewsService.remove(deleteModal.review.id);
      setDeleteModal({ open: false, review: null });
      fetchReviews();
    } catch (err) {
      alert(getApiErrorMessage(err));
    } finally {
      setDeleteLoading(false);
    }
  };

  const starRender = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`star ${i < rating ? "star--filled" : ""}`}>
        ★
      </span>
    ));
  };

  const columns: Column<Review>[] = [
    { key: "id", title: "ID", width: "60px" },
    {
      key: "product_name",
      title: "상품명",
      width: "150px",
      render: (v, row) => (
        <div
          className="product-name"
          onClick={() => {
            navigate(`/product-edit/${row.item_id}`);
          }}
        >
          {v}
        </div>
      ),
    },
    { key: "user_name", title: "작성자", width: "70px" },
    {
      key: "rating",
      title: "별점",
      width: "120px",
      render: (v) => <div className="stars">{starRender(v as number)}</div>,
    },
    {
      key: "content",
      title: "리뷰 내용",
      render: (v) => (
        <span className="review-content">
          {String(v).slice(0, 100)}
          {String(v).length > 100 ? "..." : ""}
        </span>
      ),
    },
    {
      key: "is_blind",
      title: "상태",
      width: "80px",
      render: (v) => (v ? <Badge variant="draft">블라인드</Badge> : <Badge variant="active">공개</Badge>),
    },
    {
      key: "created_at",
      title: "작성일",
      width: "110px",
      render: (v) => String(v).slice(0, 10),
    },
    {
      key: "is_blind_action",
      title: "공개",
      width: "80px",
      render: (_, row) => <Toggle checked={!row.is_blind} onChange={(checked) => handleToggleBlind(row.id, !checked)} />,
    },
    {
      key: "actions",
      title: "",
      render: (_, row) => (
        <Button size="sm" variant="outline" onClick={() => setDeleteModal({ open: true, review: row })}>
          삭제
        </Button>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader title="리뷰 관리" description="전체 리뷰를 조회하고 관리합니다." />

      <div className="admin-page__card">
        <div className="admin-page__filter">
          <Input placeholder="작성자, 상품명, 내용 검색" value={search} onChange={(e) => setSearch(e.target.value)} prefix="🔍" />
          <Select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
            options={[
              { value: "", label: "별점 전체" },
              { value: "5", label: "5점" },
              { value: "4", label: "4점" },
              { value: "3", label: "3점" },
              { value: "2", label: "2점" },
              { value: "1", label: "1점" },
            ]}
          />
          <Select
            value={blindFilter}
            onChange={(e) => setBlindFilter(e.target.value)}
            options={[
              { value: "", label: "상태 전체" },
              { value: "visible", label: "공개" },
              { value: "blinded", label: "블라인드" },
            ]}
          />
        </div>

        {error && <div className="admin-page__error">{error}</div>}
        {loading ? <div className="admin-page__loading">불러오는 중...</div> : <Table columns={columns} data={filteredReviews} rowKey="id" />}
      </div>

      {/* 삭제 확인 모달 */}
      <Modal
        open={deleteModal.open}
        title="리뷰 삭제"
        width="440px"
        onClose={() => setDeleteModal({ open: false, review: null })}
        footer={
          <div className="modal-footer-actions">
            <Button variant="secondary" onClick={() => setDeleteModal({ open: false, review: null })}>
              취소
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleteLoading}>
              {deleteLoading ? "삭제 중..." : "삭제"}
            </Button>
          </div>
        }
      >
        <p className="confirm-text">
          <strong>{deleteModal.review?.user_name}</strong>님의 리뷰를 삭제하시겠습니까?
          <br />이 작업은 되돌릴 수 없습니다.
        </p>
      </Modal>
    </div>
  );
};

export default ReviewManagement;
